import React from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import {
  LoginPage,
  RegisterPage,
  UpdatePasswordPage,
  useIsLoading,
  useUser,
} from '@dopomogai/supabase-client/react';

import { LoadingSpinner } from '../components/LoadingSpinner';
import { WebsiteV2Layout } from '../features/website-v2/layout/WebsiteV2Layout';
import { WebsiteV2PublicLayout } from '../features/website-v2/layout/WebsiteV2PublicLayout';
import { PublicPagesLayout } from '../layouts/PublicPagesLayout';

const HomePage = React.lazy(() => import('../pages/home'));
const HomepageV2Page = React.lazy(() => import('../features/website-v2/pages/HomepageV2Page'));
const SoftwareServicesPage = React.lazy(() => import('../features/website-v2/pages/SoftwareServicesPage'));
const AiRevenueAuditPage = React.lazy(() => import('../features/website-v2/pages/AiRevenueAuditPage'));
const ThankYouPage = React.lazy(() => import('../features/website-v2/pages/ThankYouPage'));
const WebsiteVNextPage = React.lazy(() => import('../pages/vnext/WebsiteVNextPage'));
const BlogIndexPage = React.lazy(() => import('../pages/blog/BlogIndexPage'));
const ArticlePage = React.lazy(() => import('../pages/blog/ArticlePage'));
const ArticlePreviewPage = React.lazy(() => import('../pages/blog/ArticlePreviewPage'));
const HubLandingPage = React.lazy(() => import('../pages/hub/HubLandingPage'));
const HubStreamsPage = React.lazy(() => import('../pages/hub/HubStreamsPage'));
const HubGuidesPage = React.lazy(() => import('../pages/hub/HubGuidesPage'));
const HubArticlesPage = React.lazy(() => import('../pages/hub/HubArticlesPage'));
const HubDemosPage = React.lazy(() => import('../pages/hub/HubDemosPage'));
const HubAssessmentPage = React.lazy(() => import('../pages/hub/HubAssessmentPage'));
const HubStreamDetailPage = React.lazy(() => import('../pages/hub/HubStreamDetailPage'));
const HubGuideDetailPage = React.lazy(() => import('../pages/hub/HubGuideDetailPage'));
const HubLearningPlanPage = React.lazy(() => import('../pages/hub/HubLearningPlanPage'));
const ConciergeEmbedGuidePage = React.lazy(() => import('../pages/docs/ConciergeEmbedGuidePage'));
const ConciergeUsageGuidePage = React.lazy(() => import('../pages/docs/ConciergeUsageGuidePage'));
const TermsOfServicePage = React.lazy(() => import('../pages/legal/TermsOfServicePage'));
const PrivacyPolicyPage = React.lazy(() => import('../pages/legal/PrivacyPolicyPage'));
const PublicOfferAgreementPage = React.lazy(() => import('../pages/legal/PublicOfferAgreementPage'));
const CustomServicesAgreementPage = React.lazy(() => import('../pages/legal/CustomServicesAgreementPage'));
const LegacyAppRoutes = React.lazy(() =>
  import('./LegacyAppRoutes').then((m) => ({ default: m.LegacyAppRoutes })),
);

function PrivateRoute({ children }: { children: React.ReactElement }) {
  const user = useUser();
  const isLoading = useIsLoading();
  const location = useLocation();

  if (isLoading) {
    return <LoadingSpinner message="Authenticating..." />;
  }

  return user ? children : <Navigate to="/auth/login" state={{ from: location }} replace />;
}

export function AppRoutes() {
  const user = useUser();
  const isLoading = useIsLoading();

  const renderAuthPage = (page: React.ReactElement) => {
    if (user) {
      return <Navigate to="/app/dashboard" replace />;
    }

    if (isLoading) {
      return <LoadingSpinner message="Authenticating..." />;
    }

    return page;
  };

  return (
    <Routes>
      <Route path="/auth" element={<Navigate to="/auth/login" replace />} />
      <Route path="/auth/login" element={renderAuthPage(<LoginPage />)} />
      <Route path="/auth/register" element={renderAuthPage(<RegisterPage />)} />
      <Route path="/auth/update-password" element={<UpdatePasswordPage />} />

      <Route element={<WebsiteV2Layout />}>
        <Route path="/software-services" element={<SoftwareServicesPage />} />
        <Route path="/ai-revenue-audit" element={<AiRevenueAuditPage />} />
        <Route path="/thank-you" element={<ThankYouPage />} />
      </Route>

      <Route element={<WebsiteV2PublicLayout />}>
        <Route path="/vnext" element={<WebsiteVNextPage />} />
        <Route path="/homepage-v2" element={<HomepageV2Page />} />
      </Route>

      <Route element={<PublicPagesLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/contact" element={<Navigate to="/#assessment" replace />} />
        <Route path="/hub" element={<HubLandingPage />} />
        <Route path="/hub/assessment" element={<HubAssessmentPage />} />
        <Route path="/hub/streams" element={<HubStreamsPage />} />
        <Route path="/hub/streams/:slug" element={<HubStreamDetailPage />} />
        <Route path="/hub/guides" element={<HubGuidesPage />} />
        <Route path="/hub/guides/:slug" element={<HubGuideDetailPage />} />
        <Route path="/hub/articles" element={<HubArticlesPage />} />
        <Route path="/hub/demos" element={<HubDemosPage />} />
        <Route path="/hub/learning-plan/:planId" element={<HubLearningPlanPage />} />
        <Route path="/blog" element={<BlogIndexPage />} />
        <Route path="/blog/preview/:token" element={<ArticlePreviewPage />} />
        <Route path="/blog/:slug" element={<ArticlePage />} />
        <Route path="/docs" element={<Navigate to="/docs/concierge-embed" replace />} />
        <Route path="/docs/concierge-embed" element={<ConciergeEmbedGuidePage />} />
        <Route path="/docs/concierge-usage" element={<ConciergeUsageGuidePage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/public-offer-agreement" element={<PublicOfferAgreementPage />} />
        <Route path="/custom-services-agreement" element={<CustomServicesAgreementPage />} />
      </Route>

      <Route path="/impact" element={<Navigate to="/" replace />} />
      <Route path="/solutions" element={<Navigate to="/" replace />} />
      <Route path="/roadmap" element={<Navigate to="/" replace />} />
      <Route path="/whitepaper" element={<Navigate to="/" replace />} />
      <Route
        path="/app/*"
        element={
          <PrivateRoute>
            <LegacyAppRoutes />
          </PrivateRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default AppRoutes;
